/**
 * BulkActionBar — sticky bar that appears when 1+ rows are selected in a table.
 * Owner runs actions immediately. Accounts/Design Ops submit as pending change
 * requests. Any other role never sees this bar (the parent decides based on
 * `/bulk/permissions`).
 */
import { useState } from 'react';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { X, Pencil, Archive, PauseCircle, Trash2, ShieldAlert } from 'lucide-react';
import { BulkModifyDialog } from './BulkModifyDialog';
import { BulkPermanentDeleteDialog } from './BulkPermanentDeleteDialog';

export function BulkActionBar({
  module,           // string — matches backend module key
  moduleLabel,      // string — shown in confirm copy
  selectedIds,     // Set<string>
  onClear,          // () => void — deselect all after action
  onApplied,        // () => Promise<void> — refetch the table
  needsApproval,    // bool — if true, buttons say "Request …"
  editableFields,   // string[] — passed to modify dialog
}) {
  const [modifyOpen, setModifyOpen] = useState(false);
  const [permanentOpen, setPermanentOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ids = [...selectedIds];
  const n = ids.length;
  if (n === 0) return null;

  const runState = async (newState) => {
    setBusy(true);
    try {
      const r = await api.post(`/bulk/${module}`, {
        ids, action: 'set_record_state', payload: { record_state: newState },
      });
      if (r.data.committed) {
        const s = r.data.summary || {};
        toast.success(
          `${moduleLabel}: ${s.applied_count} updated${s.skipped_protected?.length ? ` · ${s.skipped_protected.length} protected skipped` : ''}`
        );
      } else {
        toast.success(`Change request sent to the Owner (${r.data.target_count} row${r.data.target_count > 1 ? 's' : ''})`);
      }
      onClear();
      await onApplied?.();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(false); }
  };

  const stateLabel = (verb) => needsApproval ? `Request ${verb.toLowerCase()}` : verb;

  return (
    <>
      <div
        role="toolbar"
        aria-label={`Bulk actions for ${n} selected ${moduleLabel}`}
        data-testid={`bulk-action-bar-${module}`}
        className="sticky top-0 z-20 -mx-1 px-3 py-2 mb-3 rounded-lg border shadow-sm
                   bg-[hsl(var(--primary)/.10)] backdrop-blur
                   flex items-center gap-2 flex-wrap"
        style={{ borderColor: 'hsl(var(--primary) / .35)' }}
      >
        <span className="text-[12.5px] font-semibold text-[hsl(var(--primary))] mr-1">
          {n} selected
        </span>
        {needsApproval && (
          <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-wider text-[hsl(var(--warning))] font-semibold px-1.5 py-0.5 rounded bg-[hsl(var(--warning)/.10)]">
            <ShieldAlert size={10} />needs approval
          </span>
        )}
        <div className="flex items-center gap-1.5 ml-auto flex-wrap">
          <Button size="sm" variant="outline" disabled={busy}
                  onClick={() => setModifyOpen(true)} data-testid={`bulk-modify-${module}`}>
            <Pencil size={12} className="mr-1.5" />{needsApproval ? 'Request modify' : 'Modify'}
          </Button>
          <Button size="sm" variant="outline" disabled={busy}
                  onClick={() => runState('on_hold')} data-testid={`bulk-hold-${module}`}>
            <PauseCircle size={12} className="mr-1.5" />{stateLabel('Hold')}
          </Button>
          <Button size="sm" variant="outline" disabled={busy}
                  onClick={() => runState('archived')} data-testid={`bulk-archive-${module}`}>
            <Archive size={12} className="mr-1.5" />{stateLabel('Archive')}
          </Button>
          <Button size="sm" variant="destructive" disabled={busy}
                  onClick={() => setPermanentOpen(true)} data-testid={`bulk-delete-${module}`}>
            <Trash2 size={12} className="mr-1.5" />{stateLabel('Delete forever')}
          </Button>
          <Button size="sm" variant="ghost" onClick={onClear} aria-label="Clear selection"
                  data-testid={`bulk-clear-${module}`}>
            <X size={13} />
          </Button>
        </div>
      </div>

      {modifyOpen && (
        <BulkModifyDialog
          module={module} moduleLabel={moduleLabel}
          ids={ids} editableFields={editableFields} needsApproval={needsApproval}
          onClose={() => setModifyOpen(false)}
          onDone={async () => { setModifyOpen(false); onClear(); await onApplied?.(); }}
        />
      )}
      {permanentOpen && (
        <BulkPermanentDeleteDialog
          module={module} moduleLabel={moduleLabel} ids={ids} needsApproval={needsApproval}
          onClose={() => setPermanentOpen(false)}
          onDone={async () => { setPermanentOpen(false); onClear(); await onApplied?.(); }}
        />
      )}
    </>
  );
}
